import { CheckCircle, XCircle, AlertCircle, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';

// Use API values directly
type RecommendationType =
  | 'SAFE'
  | 'CONDITIONAL'
  | 'CAUTION'
  | 'DO_NOT_INSTALL'
  | 'approved'
  | 'approved-monitoring'
  | 'conditional'
  | 'denied'
  | 'rejected';

interface RecommendationCardProps {
  type: RecommendationType | string; // Allow any string for flexibility
  summary: string;
  conditions?: string[];
  overallScore?: number; // 0-100 composite risk score
}

const recommendationConfig: Record<
  string,
  {
    icon: typeof CheckCircle;
    label: string;
    bgClass: string;
    borderClass: string;
    iconBg: string;
    iconColor: string;
  }
> = {
  // API values (primary)
  SAFE: {
    icon: CheckCircle,
    label: 'Approved for Use',
    bgClass: 'bg-zinc-900/50',
    borderClass: 'border-green-600',
    iconBg: 'bg-green-600',
    iconColor: 'text-white',
  },
  CONDITIONAL: {
    icon: Eye,
    label: 'Approved with Monitoring',
    bgClass: 'bg-zinc-900/50',
    borderClass: 'border-yellow-600',
    iconBg: 'bg-yellow-600',
    iconColor: 'text-white',
  },
  CAUTION: {
    icon: AlertCircle,
    label: 'Conditional Approval',
    bgClass: 'bg-zinc-900/50',
    borderClass: 'border-orange-600',
    iconBg: 'bg-orange-600',
    iconColor: 'text-white',
  },
  DO_NOT_INSTALL: {
    icon: XCircle,
    label: 'Not Recommended',
    bgClass: 'bg-zinc-900/50',
    borderClass: 'border-red-600',
    iconBg: 'bg-red-600',
    iconColor: 'text-white',
  },
  // Legacy values (for backward compatibility with sample data)
  approved: {
    icon: CheckCircle,
    label: 'Approved for Use',
    bgClass: 'bg-zinc-900/50',
    borderClass: 'border-green-600',
    iconBg: 'bg-green-600',
    iconColor: 'text-white',
  },
  'approved-monitoring': {
    icon: Eye,
    label: 'Approved with Monitoring',
    bgClass: 'bg-zinc-900/50',
    borderClass: 'border-yellow-600',
    iconBg: 'bg-yellow-600',
    iconColor: 'text-white',
  },
  conditional: {
    icon: AlertCircle,
    label: 'Conditional Approval',
    bgClass: 'bg-zinc-900/50',
    borderClass: 'border-orange-600',
    iconBg: 'bg-orange-600',
    iconColor: 'text-white',
  },
  denied: {
    icon: XCircle,
    label: 'Not Recommended',
    bgClass: 'bg-zinc-900/50',
    borderClass: 'border-red-600',
    iconBg: 'bg-red-600',
    iconColor: 'text-white',
  },
  rejected: {
    icon: XCircle,
    label: 'Not Recommended',
    bgClass: 'bg-zinc-900/50',
    borderClass: 'border-red-600',
    iconBg: 'bg-red-600',
    iconColor: 'text-white',
  },
};

// Default config for unknown types
const defaultConfig = {
  icon: AlertCircle,
  label: 'Review Required',
  bgClass: 'bg-zinc-900/50',
  borderClass: 'border-yellow-600',
  iconBg: 'bg-yellow-600',
  iconColor: 'text-white',
};

const RecommendationCard = ({ type, summary, conditions, overallScore }: RecommendationCardProps) => {
  const config = recommendationConfig[type] || defaultConfig;
  const Icon = config.icon;

  return (
    <div
      className={cn('rounded-xl border-2 p-6 animate-scale-in', config.bgClass, config.borderClass)}
    >
      <div className="flex items-start gap-4">
        <div className={cn('p-3 rounded-xl', config.iconBg)}>
          <Icon className={cn('w-8 h-8', config.iconColor)} />
        </div>

        <div className="flex-1">
          <div className="flex items-baseline gap-3">
            <h3 className="text-xl font-bold mb-1 text-white">{config.label}</h3>
            {overallScore != null && (
              <span className="text-sm font-medium text-zinc-400">
                Risk Score: {overallScore}/100
              </span>
            )}
          </div>
          <p className="text-zinc-300">{summary}</p>

          {conditions && conditions.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="font-medium text-sm text-zinc-300">Required Conditions:</p>
              <ul className="space-y-1.5">
                {conditions.map((condition, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-zinc-400">
                    <div className="w-1.5 h-1.5 rounded-full bg-current mt-2 flex-shrink-0" />
                    <span>{condition}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecommendationCard;
